import {jsonToCSV} from 'react-papaparse';

import {SECTION_ORDER, SECTION_TITLES, TYPE_LABELS} from './constants';

import type {ComparisonRow} from './comparison-section';
import type {MigrationPreview} from 'src/openapi/admin/models/MigrationPreview';
import type {UserContentMigrationInfo} from 'src/openapi/admin/models/UserContentMigrationInfo';
import type {UserContentMigrationState} from 'src/openapi/admin/models/UserContentMigrationState';
import type {UserSearchesContentItem} from 'src/openapi/admin/models/UserSearchesContentItem';
import type {UserSearchesReplaceResponse} from 'src/openapi/admin/models/UserSearchesReplaceResponse';

const EMPTY_STATE: UserContentMigrationState = {
    savedSearches: [],
    emailAlerts: [],
    lists: [],
    layouts: [],
};

export type ResolveColumnsOptions = {
    section: keyof UserContentMigrationState,
    preview: MigrationPreview | null,
    hasTemplateUser: boolean,
    hasContentSelected: boolean,
    currentUserContent: UserContentMigrationState | null,
};

export type ResolvedColumns = {
    current: UserContentMigrationInfo[],
    resulting: UserContentMigrationInfo[],
};

export function resolveColumns(opts: ResolveColumnsOptions): ResolvedColumns {
    const {
        section, preview, hasTemplateUser,
        hasContentSelected, currentUserContent,
    } = opts;
    const current = (currentUserContent ?? EMPTY_STATE)[section];

    if (!hasTemplateUser || !hasContentSelected || !preview) {
        return {current, resulting: current};
    }

    const templateHasContent = Object.values(
        preview.predictUserState,
    ).some(arr => arr.length > 0);

    if (!templateHasContent) {
        return {current, resulting: []};
    }

    return {
        current: preview.currentUserState[section],
        resulting: preview.predictUserState[section],
    };
}

export type ResultSection = {
    key: string,
    title: string,
    rows: ComparisonRow[],
};

export function groupByType(
    items: UserSearchesContentItem[],
): Record<string, UserSearchesContentItem[]> {
    const map: Record<string, UserSearchesContentItem[]> = {};
    for (const item of items) {
        (map[item.type] ??= []).push(item);
    }
    return map;
}

function collectTypes(
    oldByType: Record<string, UserSearchesContentItem[]>,
    newByType: Record<string, UserSearchesContentItem[]>,
): string[] {
    const knownTypes = Object.keys(oldByType).concat(
        Object.keys(newByType),
    );
    return SECTION_ORDER.concat(
        knownTypes.filter(t => !SECTION_ORDER.includes(t)),
    );
}

function forEachZipped<T>(
    oldItems: UserSearchesContentItem[],
    newItems: UserSearchesContentItem[],
    cb: (
        oldItem: UserSearchesContentItem | undefined,
        newItem: UserSearchesContentItem | undefined,
    ) => T,
): T[] {
    const maxLen = Math.max(oldItems.length, newItems.length);
    const result: T[] = [];
    for (let i = 0; i < maxLen; i++) {
        result.push(cb(oldItems[i], newItems[i]));
    }
    return result;
}

export function buildSections(
    result: UserSearchesReplaceResponse,
): ResultSection[] {
    const oldByType = groupByType(result.oldValue ?? []);
    const newByType = groupByType(result.newValue ?? []);

    return collectTypes(oldByType, newByType).map(type => ({
        key: type,
        title: SECTION_TITLES[type] ?? TYPE_LABELS[type] ?? type,
        rows: forEachZipped(
            oldByType[type] ?? [],
            newByType[type] ?? [],
            (old, cur) => ({
                left: old?.name ?? '',
                right: cur?.name ?? '',
            }),
        ),
    }));
}

export const CSV_HEADERS = {
    type: 'Type',
    oldValueId: 'Old Value ID',
    oldValue: 'Old Value',
    newValueId: 'New Value ID',
    newValue: 'New Value',
};

export function buildCsvBody(
    oldValue: UserSearchesContentItem[],
    newValue: UserSearchesContentItem[],
): Record<string, string>[] {
    const oldByType = groupByType(oldValue);
    const newByType = groupByType(newValue);

    return collectTypes(oldByType, newByType).flatMap(type =>
        forEachZipped(
            oldByType[type] ?? [],
            newByType[type] ?? [],
            (old, cur) => ({
                type: TYPE_LABELS[type] ?? type,
                oldValueId: old?.id ?? '',
                oldValue: old?.name ?? '',
                newValueId: cur?.id ?? '',
                newValue: cur?.name ?? '',
            }),
        ),
    );
}

export function hasMigrationResult(
    result: UserSearchesReplaceResponse | null | undefined,
): boolean {
    if (!result) {
        return false;
    }
    const oldLen = result.oldValue?.length ?? 0;
    const newLen = result.newValue?.length ?? 0;
    return oldLen > 0 || newLen > 0;
}

export function downloadMigrationCsv(
    result: UserSearchesReplaceResponse,
    filename: string,
): void {
    const body = buildCsvBody(
        result.oldValue ?? [],
        result.newValue ?? [],
    );
    const csv = jsonToCSV([CSV_HEADERS, ...body], {
        delimiter: ',',
        header: false,
        newline: '\r\n',
        quotes: true,
        skipEmptyLines: true,
    });
    const blob = new Blob([csv], {type: 'text/csv'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
